import { Effect, pipe } from 'effect';

// Shared
import { MESSAGES, MasterCategoryType } from '@shared/constants';

// Constants
import { END_POINTS } from '@/constants/endpoint';

// Services
import { httpClient } from '@/services/http';

// Utils
import {
  getCategoryItems,
  getCertificationOrganizations,
  createCategoryOptions,
} from '@shared/utils';

// Types
import { MasterDataResponse, OptionDataType } from '@shared/types';
import { RequestConfig } from '@/types';

const DEFAULT_OPTION_DATA: OptionDataType = {
  technicalSkills: [],
  technicalSkillCategories: [],
  socialLinks: [],
  degrees: [],
  schools: [],
  jobTitles: [],
  fieldsOfStudy: [],
  certificationNames: [],
  certificationOrganizations: [],
};

export const getOptionData = async (
  types: MasterCategoryType[],
  requestConfig?: RequestConfig,
) =>
  Effect.runPromise(
    pipe(
      Effect.tryPromise({
        try: async () => {
          const response = await httpClient<MasterDataResponse>(
            END_POINTS.GET_MASTER_DATA(types),
            {
              ...requestConfig,
            },
          );

          if (response.error || !response.data) {
            return {
              data: { ...DEFAULT_OPTION_DATA },
              error: response.error || MESSAGES.FETCH_OPTIONS_DATA_ERROR,
            };
          }

          const certificationNames = getCategoryItems(
            response.data,
            MasterCategoryType.CERTIFICATIONS,
          );

          const certificationOrganizations =
            getCertificationOrganizations(certificationNames);

          // Get technical skills from the 'skills' category in the new response format
          const technicalSkills = getCategoryItems(
            response.data,
            MasterCategoryType.SKILLS,
          );

          // Extract skill categories from the skills data
          const skillCategories =
            response.data.skills?.map((skill) => skill.name) || [];
          const technicalSkillCategories =
            createCategoryOptions(skillCategories);

          const categorizedData: OptionDataType = {
            technicalSkills,
            technicalSkillCategories,
            socialLinks: getCategoryItems(
              response.data,
              MasterCategoryType.SOCIAL_LINKS,
            ),
            degrees: getCategoryItems(
              response.data,
              MasterCategoryType.DEGREES,
            ),
            schools: getCategoryItems(
              response.data,
              MasterCategoryType.SCHOOLS,
            ),
            jobTitles: getCategoryItems(
              response.data,
              MasterCategoryType.JOB_TITLES,
            ),
            fieldsOfStudy: getCategoryItems(
              response.data,
              MasterCategoryType.FIELDS_OF_STUDY,
            ),
            certificationNames,
            certificationOrganizations,
          };

          return {
            data: { ...categorizedData },
            error: null,
          };
        },
        catch: (error) => ({
          data: { ...DEFAULT_OPTION_DATA },
          error:
            error instanceof Error ? error.message : MESSAGES.GENERAL_ERROR,
        }),
      }),
      Effect.catchAll((error) => Effect.succeed(error)),
    ),
  );
